// Modal de búsqueda global - Versión Mobile - Global search modal mobile version
// Versión fullscreen para dispositivos móviles con header y botón de volver atrás
import {
  faBagShopping,
  faBoxOpen,
  faCartFlatbed,
  faCartShopping,
  faFileLines,
  faBoxesStacked,
  faShelves,
  faStore,
  faXmark,
  faMagnifyingGlass,
  faChevronLeft,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Spinner } from '@shared/components/ui/Spinner';
import { useSidebar } from '@shared/components/ui/sidebar';
import * as Icons from '@shared/components/ui/icons/warehouse.icons';
import { StatusBadgeWithDot } from '@modules/Orders/List/components/display/StatusBadgeWithDot';
import { defaultOrderStatusDot, statusColorMap } from '@modules/Orders/List/config/orderStatusConfig';
import { MerchandiseStatusBadge } from '@modules/Merchandise/components/MerchandiseStatusBadge';
import { getPreparationStatusColor, usePreparationStatusName } from '@modules/OrderPreparation/shared/config';
import { getReturnedStatusBadgeClass, getReturnedStatusName } from '@modules/Returned/List/config/returnedConfig';

import { createGlobalSearchAdapter } from './adapters/GlobalSearchAdapter';
import type { GlobalSearchResult } from './models/GlobalSearch';
import { getGlobalSearchRoute } from './utils/getGlobalSearchRoute';

// Componente para badge de estado de preparación - Preparation status badge component
const PreparationStatusBadge: React.FC<{ status: number }> = ({ status }) => {
  const statusName = usePreparationStatusName(status);
  const statusColor = getPreparationStatusColor(status);

  return (
    <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
      {statusName}
    </Badge>
  );
};

interface GlobalSearchModalMobileProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export const GlobalSearchModalMobile: React.FC<GlobalSearchModalMobileProps> = ({
  open,
  onOpenChange,
  searchQuery,
  onSearchQueryChange,
}) => {
  const { t } = useTranslation('menu');
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  const modalRef = useRef<ModalCenterRef>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const globalSearchAdapter = useMemo(() => createGlobalSearchAdapter(), []);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Controlar el modal con el ref - Control modal with ref
  useEffect(() => {
    if (open) {
      modalRef.current?.openModal();
      // Enfocar el input cuando se abre el modal - Focus input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      modalRef.current?.closeModal();
    }
  }, [open]);

  // Manejar cambio en el input de búsqueda - Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchQueryChange(e.target.value);
  };

  // Manejar reset de búsqueda - Handle search reset
  const handleResetSearch = () => {
    onSearchQueryChange('');
    searchInputRef.current?.focus();
  };

  // Manejar cierre del modal - Handle modal close
  const handleClose = () => {
    onOpenChange(false);
  };

  // Búsqueda con infinite scroll usando React Query - Search with infinite scroll using React Query
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['globalSearch', searchQuery],
    queryFn: async ({ pageParam = 1 }) => {
      if (!searchQuery.trim()) {
        return {
          items: [],
          total: 0,
          currentPage: 1,
          lastPage: 1,
          hasNextPage: false,
          nextPage: null,
        };
      }
      return globalSearchAdapter.search(searchQuery, pageParam, 15);
    },
    enabled: open && searchQuery.trim().length > 0,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 0,
    gcTime: 0,
  });

  // Obtener todos los resultados en una lista plana - Get all results in a flat list
  const allResults = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data]);

  // Obtener el total de resultados de la primera página - Get total results from first page
  const totalResults = useMemo(() => {
    return data?.pages?.[0]?.total ?? 0;
  }, [data]);

  // Infinite scroll con IntersectionObserver - Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px',
      },
    );

    const loadMoreElement = loadMoreRef.current;
    if (loadMoreElement) {
      observer.observe(loadMoreElement);
    }

    return () => {
      if (loadMoreElement) {
        observer.unobserve(loadMoreElement);
      }
    };
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Manejar click en resultado - Handle result click
  const handleResultClick = (result: GlobalSearchResult) => {
    const route = getGlobalSearchRoute(result.type, result.id, result.statusName, result.code);
    // 🎯 Usar replace: false para forzar actualización incluso si la ruta base es la misma
    navigate(route, { replace: false });
    onSearchQueryChange(''); // 🎯 Limpiar el input de búsqueda
    onOpenChange(false);
    // Cerrar el navbar en mobile - Close navbar on mobile
    setOpenMobile(false);
  };

  // Obtener icono según el tipo - Get icon by type
  const typeIcons = useMemo(
    () => ({
      product: <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3 text-icons light:text-title" />,
      cart: <FontAwesomeIcon icon={faCartFlatbed} className="w-3 h-3 text-icons light:text-title" />,
      order: <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3 text-icons light:text-title" />,
      merchandise: <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3 text-icons light:text-title" />,
      location: <FontAwesomeIcon icon={faShelves} className="w-3 h-3 text-icons light:text-title" />,
      preparation: <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3 text-icons light:text-title" />,
      returned_reception: <Icons.RehusableIcon className="w-3 h-3 text-icons light:text-title" />,
      default: <FontAwesomeIcon icon={faFileLines} className="w-3 h-3 text-icons light:text-title" />,
    }),
    [],
  );

  const getIconNode = (type: string) => (typeIcons as Record<string, React.ReactNode>)[type] || typeIcons.default;

  // Obtener label de categoría - Get category label
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'product':
        return t('global_search.category.product');
      case 'cart':
        return t('global_search.category.cart');
      case 'order':
        return t('global_search.category.order');
      case 'merchandise':
        return t('global_search.category.merchandise');
      case 'location':
        return t('global_search.category.location');
      case 'preparation':
        return t('global_search.category.preparation');
      case 'returned_reception':
        return t('global_search.category.returned_reception');
      default:
        return category;
    }
  };

  // Mapear nombre de estado a ID numérico para obtener colores - Map status name to numeric ID for colors
  const getStatusId = (statusName: string | number | undefined): number | null => {
    if (!statusName) return null;

    if (typeof statusName === 'number') {
      return statusName;
    }

    const normalized = statusName
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const statusNameMap: Record<string, number> = {
      'por confirmar': 1,
      pendiente: 2,
      'por preparar': 3,
      'en preparacion': 4,
      'en preparación': 4,
      picking: 4,
      packing: 5,
      preparado: 6,
      enviado: 7,
      devuelto: 8,
      devuélto: 8,
      cancelado: 9,
    };

    return statusNameMap[normalized] || null;
  };

  const getOrderStatusDotClass = (statusName: string | number | undefined): string => {
    const statusId = getStatusId(statusName);
    if (!statusId || !statusColorMap[statusId]) {
      return defaultOrderStatusDot.dot;
    }
    return statusColorMap[statusId].dot;
  };

  // Obtener clase del badge para recepciones devueltas - Get badge class for returned receptions (helper function)
  const getReturnedStatusBadgeClassHelper = (status: number) => {
    return getReturnedStatusBadgeClass(status);
  };

  // Obtener nombre del estado para recepciones devueltas - Get status name for returned receptions (helper function)
  const getReturnedStatusNameHelper = (status: number) => {
    return getReturnedStatusName(status, t);
  };

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <ModalCenter
      ref={modalRef}
      type="default"
      width="lg"
      onCloseModal={handleClose}
      fullScreenMobile={true}
      className="bg-body-bg [&_*]:focus:outline-none [&_*]:focus-visible:outline-none [&_[data-testid*='header']]:!hidden"
      classNameBody="!p-0 flex flex-col h-full"
      data-testid="global-search-modal-mobile"
    >
      {/* Header personalizado con botón de volver atrás - Custom header with back button */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-card-border">
        {/* Botón de volver atrás - Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="p-2 -ml-2"
          data-testid="global-search-mobile-back"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4 text-icons" />
        </Button>
        {/* Título - Title */}
        <h2 className="text-lg font-medium text-title flex-1">{t('global_search.title')}</h2>
      </div>

      {/* Contenido del modal - Modal content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Input de búsqueda - Search input */}
        <div className="px-4 pt-4 pb-3">
          <div className="relative">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={t('global_search.mobile_placeholder')}
              value={searchQuery}
              onChange={handleSearchInputChange}
              className={`w-full !border-0 bg-card-bg hover:bg-hover-press focus-visible:ring-0 focus-visible:outline-none h-auto text-base font-medium text-title px-4 py-3 !rounded-lg transition-colors ${
                searchQuery && !isLoading && totalResults > 0 ? 'pr-28' : searchQuery ? 'pr-10' : 'pr-4'
              }`}
              data-testid="global-search-modal-mobile-input"
            />

            {/* Total de resultados y botón para resetear búsqueda - Total results and reset search button */}
            {searchQuery && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {/* Total de resultados - Total results */}
                {!isLoading && totalResults > 0 && (
                  <span className="text-sm text-complementary-text light:text-title whitespace-nowrap">
                    {totalResults} {totalResults === 1 ? t('global_search.result') : t('global_search.results')}
                  </span>
                )}
                {/* Botón para resetear búsqueda - Reset search button */}
                <button
                  onClick={handleResetSearch}
                  className="p-1 hover:bg-hover-press rounded transition-colors flex-shrink-0"
                  data-testid="global-search-mobile-reset"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-icons" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Results List - Lista de resultados */}
        {hasSearchQuery && (
          <div ref={resultsListRef} className="flex-1 overflow-y-auto px-4" data-testid="global-search-mobile-results">
            {isLoading ? (
              <div className="px-4 py-8 flex flex-col items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : allResults.length === 0 ? (
              <div className="px-4 py-6 flex flex-col items-center justify-center">
                {/* Icono de lupa grande - Large magnifying glass icon */}
                <div className="mb-6 text-card-border">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="w-20 h-20" />
                </div>
                {/* Título principal - Main title */}
                <h3 className="text-lg font-bold text-title mb-2">{t('global_search.no_results_title')}</h3>
                {/* Descripción - Description */}
                <p className="text-sm text-complementary-text text-center max-w-sm">
                  {t('global_search.no_results_description')}
                </p>
              </div>
            ) : (
              <div className="py-2 flex flex-col gap-1">
                {/* Lista única de resultados - Single results list */}
                {allResults.map((result, index) => {
                  return (
                    <div
                      key={`${result.type}-${result.id}`}
                      data-result-index={index}
                      onClick={() => handleResultClick(result)}
                      className="px-4 py-3 flex items-start gap-4 cursor-pointer transition-all rounded-md border border-transparent hover:border-card-border active:bg-hover-press"
                      data-testid={`global-search-mobile-result-${index}`}
                    >
                      {/* Columna izquierda: icono + categoría */}
                      <div className="flex items-start gap-2 flex-shrink-0 pt-0.5 w-24">
                        {getIconNode(result.type)}
                        <span className="text-xs text-icons light:text-title">{getCategoryLabel(result.type)}</span>
                      </div>

                      {/* Columna derecha: contenido */}
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        {/* Título - Title */}
                        {result.type !== 'cart' && (
                          <div className="text-sm font-medium text-title truncate">{result.title}</div>
                        )}

                        {/* Línea secundaria con imagen y badge - Secondary line with image and badge */}
                        {(result.type === 'location' ||
                          result.type === 'preparation' ||
                          result.type === 'returned_reception' ||
                          result.subtitle ||
                          result.code ||
                          result.imageUrl ||
                          result.metadata ||
                          result.productName ||
                          typeof result.quantity === 'number') && (
                          <div className="flex items-center gap-3">
                            {result.type === 'product' && result.imageUrl && (
                              <img
                                src={result.imageUrl}
                                alt={result.title}
                                className="w-6 h-6 rounded-full object-cover bg-card-bg flex-shrink-0"
                              />
                            )}

                            {result.type === 'order' ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                {result.storeName && (
                                  <Badge className="inline-flex items-center gap-1 bg-hover-press text-title text-xs px-2 py-0.5 rounded-full border-0">
                                    <FontAwesomeIcon icon={faStore} className="w-2.5 h-2.5 text-icons" />
                                    {result.storeName}
                                  </Badge>
                                )}
                                {result.statusName && (
                                  <StatusBadgeWithDot
                                    className="text-xs rounded-full"
                                    dotClassName={getOrderStatusDotClass(result.statusName)}
                                    label={result.statusName}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                {result.type === 'location' ? (
                                  <>
                                    <div className="text-xs text-complementary-text truncate">
                                      {result.productName || t('global_search.no_product')}
                                    </div>
                                    <Badge
                                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border-0 ${
                                        (typeof result.quantity === 'number' ? result.quantity : 0) > 0
                                          ? 'bg-card-bg text-success'
                                          : 'bg-hover-press text-complementary-text'
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faBoxesStacked}
                                        className={`w-3 h-3 ${
                                          (typeof result.quantity === 'number' ? result.quantity : 0) > 0
                                            ? 'text-success'
                                            : 'text-complementary-text'
                                        }`}
                                      />
                                      {typeof result.quantity === 'number' ? result.quantity : 0}
                                    </Badge>
                                  </>
                                ) : (
                                  <>
                                    {result.type !== 'product' &&
                                      result.type !== 'cart' &&
                                      result.type !== 'merchandise' &&
                                      result.type !== 'preparation' &&
                                      result.type !== 'returned_reception' &&
                                      result.subtitle && (
                                        <div className="text-xs text-complementary-text truncate">
                                          {result.subtitle}
                                        </div>
                                      )}
                                    {result.metadata && (
                                      <div className="text-xs text-complementary-text truncate">{result.metadata}</div>
                                    )}
                                    {result.type === 'product' && result.code && (
                                      <Badge className="text-xs px-2 py-0.5 rounded-full bg-bg-info text-info flex-shrink-0 uppercase -ml-1 border-0">
                                        {result.code}
                                      </Badge>
                                    )}
                                    {result.type === 'cart' && result.code && (
                                      <Badge className="text-xs px-2 py-0.5 rounded-full bg-bg-success text-success flex-shrink-0 border-0">
                                        {result.code}
                                      </Badge>
                                    )}
                                    {result.type === 'merchandise' && typeof result.statusName === 'number' && (
                                      <MerchandiseStatusBadge
                                        status={result.statusName}
                                        size="sm"
                                        className="!border-0"
                                      />
                                    )}
                                    {result.type === 'preparation' && typeof result.statusName === 'number' && (
                                      <PreparationStatusBadge status={result.statusName} />
                                    )}
                                    {result.type === 'returned_reception' && (
                                      <Badge
                                        className={`text-xs px-2 py-0.5 rounded-full border-0 ${getReturnedStatusBadgeClassHelper(
                                          typeof result.statusName === 'number' ? result.statusName : 1,
                                        )}`}
                                      >
                                        {getReturnedStatusNameHelper(
                                          typeof result.statusName === 'number' ? result.statusName : 1,
                                        )}
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Elemento de referencia para infinite scroll - Reference element for infinite scroll */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-4 flex justify-center">
                    {isFetchingNextPage && <Spinner size="sm" />}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ModalCenter>
  );
};
